import React, { useState, useEffect, useRef } from "react";
import CheckBoxGrey from "../textfields/CheckBoxGrey";
import Icon1 from "../icons/Icon";
import SecondaryButton from "../buttons/SecondaryButton";

// Add column configuration button
const DataGridGPT2 = ({
  columns,
  initialPage = 0,
  initialPageSize = 50,
  show_ranking = false,
  rows,
  onClick = () => {},
  is_folders = false,
  searchValue = "",
  initial_sort_field = "id",
  initial_asc = "asc",
  handleSortChange = () => {},
  handleSelectionModelChange = () => {},
  initial_saved_columns_sizes = {},
  initial_saved_columns_showed = is_folders
    ? {
        id: false,
        num: false,
        creation_date: false,
        state: false,
      }
    : { id: false }, // Set the default initial column visibility

  setPaginationModel = () => {},
  handleColumnManagementChange = () => {},
  handleColumnSizeChange = () => {},
  rowHeight = 40,
  selectable = true,
  orgData,
  handleResetConfig = () => {},
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [rowSelectionModel, onRowSelectionModelChange] = useState([]);

  // Column configuration dropdown state
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const columnConfigRef = useRef(null);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: initial_sort_field,
    direction: initial_asc,
  });

  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    is_folders
      ? {
          ...initial_saved_columns_showed,
          state: searchValue === "" ? false : true,
        }
      : initial_saved_columns_showed
  );

  // Column sizes state
  const [columnSizes, setColumnSizes] = useState(initial_saved_columns_sizes);

  // Initialize flag
  const [initialised, setInitialised] = useState(false);

  // References to prevent infinite loops
  const isFirstRender = useRef(true);
  const prevSearchValue = useRef(searchValue);

  const scrollRef = useRef(null);

  const resetScroll = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // Selection state

  // Handle click outside column config dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        columnConfigRef.current &&
        !columnConfigRef.current.contains(event.target)
      ) {
        setColumnConfigOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize component
  useEffect(() => {
    setInitialised(true);
  }, []);

  // Effect to update pagination model when page or size changes

  // Effect to set initial column visibility
  useEffect(() => {
    if (prevSearchValue.current !== searchValue) {
      prevSearchValue.current = searchValue;

      const new_column_visibility_model = { ...columnVisibilityModel };

      if (is_folders) {
        if (searchValue !== "") {
          new_column_visibility_model["state"] = true;
        } else {
          new_column_visibility_model["state"] = false;
        }
      }

      setColumnVisibilityModel(new_column_visibility_model);

      // Use a timeout to prevent circular updates
      const timeoutId = setTimeout(() => {
        handleColumnManagementChange(new_column_visibility_model);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [
    searchValue,
    is_folders,
    columnVisibilityModel,
    handleColumnManagementChange,
  ]);

  // Pagination logic
  const totalPages = Math.ceil(rows.length / pageSize);

  function lowerAndTrim(str, key = null) {
    if (key === "all_tags") {
      let tags = str?.filter((item) => item?.org_id === orgData?.id);
      let tag_str = tags?.map((item) => item?.nom);
      //return str?.map((item) => item.toLowerCase().trim());
      return tag_str;
    }
    if (typeof str !== "string") {
      return str;
    }
    return str?.toLowerCase()?.trim();
  }

  // Sorting logic
  const sortedRows = React.useMemo(() => {
    const sortableRows = [...rows];
    if (sortConfig.key !== null) {
      sortableRows.sort((a, b) => {
        if (
          lowerAndTrim(a[sortConfig.key], sortConfig.key) <
          lowerAndTrim(b[sortConfig.key], sortConfig.key)
        ) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (
          lowerAndTrim(a[sortConfig.key], sortConfig.key) >
          lowerAndTrim(b[sortConfig.key], sortConfig.key)
        ) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableRows;
  }, [rows, sortConfig]);
  const paginatedRows = sortedRows.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  // Handler for sort request
  const requestSort = (key) => {
    if (!initialised) {
      setInitialised(true);
    }

    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    // Call external sort handler
    handleSortChange(key && direction ? [{ field: key, sort: direction }] : []);
  };

  // Toggle a single column's visibility
  const toggleColumnVisibility = (field) => {
    const new_model = { ...columnVisibilityModel };
    new_model[field] = !new_model[field];

    setColumnVisibilityModel(new_model);

    // Use a timeout to prevent circular updates
    setTimeout(() => {
      handleColumnManagementChange(new_model);
    }, 0);
  };

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
    setPaginationModel({
      page: newPage,
      pageSize: pageSize,
    });

    resetScroll();
  };

  // Handler for column resize
  const handleColumnResize = (field, newWidth) => {
    const newSizes = {
      ...columnSizes,
      [field]: newWidth,
    };
    setColumnSizes(newSizes);

    // Use a timeout to prevent potential circular updates
    handleColumnSizeChange({
      colDef: {
        width: newWidth,
        field: field,
      },
    });
  };

  // Row selection handler
  const handleRowSelectionChange = (id) => {
    let newSelection;

    if (rowSelectionModel.includes(id)) {
      // If row is already selected, remove it
      newSelection = rowSelectionModel.filter((rowId) => rowId !== id);
    } else {
      // If row is not selected, add it to selection
      newSelection = [...rowSelectionModel, id];
    }

    // Use a timeout to prevent potential circular updates

    onRowSelectionModelChange(newSelection);
    handleSelectionModelChange(newSelection);
  };

  // Handler for row click
  const handleRowClick = (row) => {
    onClick(row);
  };

  // Function to calculate column width based on width or flex
  const getColumnWidth = (column) => {
    // Use custom saved width if available
    if (columnSizes[column.field] !== undefined) {
      return `${columnSizes[column.field]}px`;
    }
    // Use explicit width if provided
    if (column.width) {
      return `${column.width}px`;
    }

    // Use flex proportional width if provided
    if (column.flex) {
      // For flex, we'll convert it to a percentage of the table width
      // This is a simplified approach - actual flex would need container refs
      return `${(column.flex * 100) / totalFlexUnits}%`;
    }

    // Default width if nothing specified
    return "auto";
  };

  // Calculate total flex units once
  const totalFlexUnits = React.useMemo(() => {
    return columns.reduce((total, column) => total + (column.flex || 0), 0);
  }, [columns]);

  // Helper function to render cell content
  const renderCellContent = (column, row) => {
    // If the column has a renderCell function, use it
    if (column.renderCell) {
      // Create params object similar to MUI DataGrid
      const params = {
        value: row[column.field],
        row: row,
        field: column.field,
        colDef: column,
      };
      return column.renderCell(params);
    }

    const value = row[column.field];

    // Handle date type columns
    if (column.type === "date" && value instanceof Date) {
      return value.toLocaleDateString();
    }

    // Handle date strings that need to be converted
    if (column.type === "date" && typeof value === "string") {
      try {
        const date = new Date(value);
        return date.toLocaleDateString();
      } catch (e) {
        return value; // Return original value if date conversion fails
      }
    }

    // Don't render objects
    if (
      typeof value === "object" &&
      value !== null &&
      !(value instanceof Date)
    ) {
      return "[Object]";
    }

    // Otherwise, just return the cell value
    return value;
  };

  useEffect(() => {
    if (rows.length !== 0) {
      handleChangePage(initialPage < totalPages ? initialPage : 0);
    }
  }, [rows]);

  // Get visible columns
  const visibleColumns = React.useMemo(() => {
    return columns.filter((col) => columnVisibilityModel[col.field] !== false);
  }, [columns, columnVisibilityModel]);

  return (
    <div className="w-full h-full max-h-full border max-w-full border-gray-200 rounded-lg  flex flex-col">
      <div className="w-full overflow-hidden ">
        <div className="w-full pb-10">
          <div className="w-full bg-gray-50 sticky top-0 ">
            <table
              className="w-full border-collapse"
              style={{ tableLayout: "fixed", borderSpacing: 0 }}
            >
              <colgroup>
                {selectable && (
                  <col
                    style={{
                      width: "40px",
                    }}
                  />
                )}

                {show_ranking && (
                  <col
                    style={{
                      width: "40px",
                    }}
                  />
                )}

                {visibleColumns.map((column) => (
                  <col
                    key={`col-${column.field}`}
                    style={{ width: getColumnWidth(column) }}
                  />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {selectable && (
                    <th
                      style={{
                        width: "40px",
                        minWidth: "40px",
                        maxWidth: "40px",
                        padding: 0,
                        overflow: "hidden",
                        boxSizing: "border-box",
                        position: "sticky",
                        left: 0,
                        background: "#f9fafb",
                        zIndex: 1,
                      }}
                    >
                      <CheckBoxGrey
                        checked={
                          rows.length > 0 &&
                          rowSelectionModel.length === rows.length
                        }
                        onChange={(checked) => {
                          if (checked) {
                            const allIds = rows.map((row) => row.id);

                            onRowSelectionModelChange(allIds);
                            handleSelectionModelChange(allIds);
                          } else {
                            onRowSelectionModelChange([]);
                            handleSelectionModelChange([]);
                          }
                        }}
                      ></CheckBoxGrey>
                    </th>
                  )}
                  {show_ranking && (
                    <col
                      style={{
                        width: "40px",
                        maxWidth: "40px",
                        minWidth: "40px",
                      }}
                    />
                  )}
                  {visibleColumns.map((column) => (
                    <th
                      key={column.field}
                      className={`
                        ${sortConfig.key === column.field ? "border-b-2 border-indigo-500" : ""}
                        
                        text-left pr-4 text-gray-500 p-2 font-medium text-h3 uppercase w-fit relative cursor-pointer whitespace-nowrap select-none`}
                      onClick={() => requestSort(column.field)}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="text-ellipsis overflow-hidden whitespace-nowrap">
                          {column.headerName}
                        </span>
                        {sortConfig.key === column.field && (
                          <span className="pl-0 rounded-full">
                            {sortConfig.direction === "asc" ? (
                              <Icon1
                                icon_name="up2"
                                size={15}
                                fill="black"
                              ></Icon1>
                            ) : (
                              <Icon1
                                icon_name="down2"
                                size={15}
                                fill="black"
                              ></Icon1>
                            )}
                          </span>
                        )}
                      </div>
                      <div
                        className="absolute right-0 top-0 h-full w-1 rounded-lg bg-gray-100 cursor-col-resize hover:bg-indigo-300"
                        onMouseDown={(e) => {
                          const startX = e.pageX;
                          const startWidth =
                            columnSizes[column.field] || column.width || 100;

                          const onMouseMove = (moveEvent) => {
                            const newWidth = Math.max(
                              80,
                              startWidth + (moveEvent.pageX - startX)
                            );
                            handleColumnResize(column.field, newWidth);
                          };

                          const onMouseUp = () => {
                            document.removeEventListener(
                              "mousemove",
                              onMouseMove
                            );
                            document.removeEventListener("mouseup", onMouseUp);
                          };

                          document.addEventListener("mousemove", onMouseMove);
                          document.addEventListener("mouseup", onMouseUp);
                        }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>

          <div
            ref={scrollRef}
            className="w-full overflow-auto pb-24"
            style={{ maxHeight: "calc(100vh - 200px)" }}
          >
            <table
              className="w-full border-collapse"
              style={{ tableLayout: "fixed", borderSpacing: 0 }}
            >
              <colgroup>
                {selectable && (
                  <col
                    style={{
                      width: "40px",
                      maxWidth: "40px",
                      minWidth: "40px",
                    }}
                  />
                )}
                {show_ranking && (
                  <col
                    style={{
                      width: "40px",
                      maxWidth: "40px",
                      minWidth: "40px",
                    }}
                  />
                )}

                {visibleColumns.map((column) => (
                  <col
                    key={`col-body-${column.field}`}
                    style={{ width: getColumnWidth(column) }}
                  />
                ))}
              </colgroup>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRows.map((row, rowIndex) => (
                  <tr
                    key={row.id || rowIndex}
                    className={`hover:bg-gray-50 cursor-pointer ${
                      rowSelectionModel.includes(row.id) ? "bg-indigo-50" : ""
                    }`}
                    onClick={() => handleRowClick(row)}
                    style={{ height: `${rowHeight}px` }}
                  >
                    {selectable && (
                      <td
                        style={{
                          width: "40px",
                          minWidth: "40px",
                          maxWidth: "40px",
                          padding: 0,
                          overflow: "hidden",
                          boxSizing: "border-box",
                          position: "sticky",
                          left: 0,
                          background: rowSelectionModel.includes(row.id)
                            ? "#eff6ff"
                            : "#fff",
                          zIndex: 1,
                        }}
                      >
                        <CheckBoxGrey
                          checked={rowSelectionModel.includes(row.id)}
                          onChange={() => {
                            handleRowSelectionChange(row.id);
                          }}
                        ></CheckBoxGrey>
                      </td>
                    )}
                    {show_ranking && (
                      <td
                        style={{
                          width: "40px",
                          minWidth: "40px",
                          maxWidth: "40px",
                        }}
                        className="text-h3 text-shadow_50"
                      >
                        {rowIndex + 1}
                      </td>
                    )}
                    {visibleColumns.map((column) => (
                      <td
                        key={`${row.id || rowIndex}-${column.field}`}
                        style={{
                          padding: "8px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          boxSizing: "border-box",
                        }}
                        className="text-h3 text-gray-500"
                      >
                        {renderCellContent(column, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white px-4 py-2 flex items-center h-fit justify-between border-1 border-gray-200 sm:px-6 mt-auto">
        <div className="flex-1 flex h-fit justify-between sm:hidden">
          <button
            onClick={() => {
              const newPage = Math.max(0, currentPage - 1);
              handleChangePage(newPage);
            }}
            disabled={currentPage === 0}
            className="relative inline-flex items-center px-4 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>
          <button
            onClick={() => {
              const newPage = Math.min(totalPages - 1, currentPage + 1);
              handleChangePage(newPage);
            }}
            disabled={currentPage === totalPages - 1}
            className="ml-3 relative inline-flex items-center px-4 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
          </button>
        </div>
        <div className="flex justify-end pr-2">
          <div className="relative" ref={columnConfigRef}>
            <button
              onClick={() => setColumnConfigOpen(!columnConfigOpen)}
              className="py-1 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center space-x-1"
            >
              <span>Colonnes</span>
              <Icon1 icon_name="settings" size={15} fill="black"></Icon1>
            </button>

            {columnConfigOpen && (
              <div className="absolute bottom-8 z-10 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                <div className="px-3 py-1  font-medium text-gray-700">
                  Colonnes visibles
                </div>
                <div className="px-3 py-2 border-b border-gray-200 font-medium text-gray-700">
                  <SecondaryButton
                    onClick={() => {
                      handleResetConfig();
                    }}
                  >
                    Réinitialiser
                  </SecondaryButton>
                </div>
                <div className="max-h-60 overflow-y-auto py-1">
                  {columns.map((column) => (
                    <div
                      key={column.field}
                      className="px-3 py-1 hover:bg-gray-100"
                    >
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <CheckBoxGrey
                          checked={
                            columnVisibilityModel[column.field] !== false
                          }
                          onChange={() => toggleColumnVisibility(column.field)}
                        />
                        <span className="text-sm">{column.headerName}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2">
            {/* Page size selector */}

            <div className="border border-gray-300 rounded-md text-sm py-1 px-2 cursor-default select-none">
              <span className="font-medium">{rows.length}</span>
            </div>
            <select
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
                console.log("Page size changed to:", newSize);
                handleChangePage(0); // Reset to first page on page size change
              }}
              className="border border-gray-300 rounded-md text-sm py-1 px-2"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>

            <nav
              className="relative z-0 inline-flex h-8 rounded-md -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={() => {
                  const newPage = Math.max(0, currentPage - 1);
                  handleChangePage(newPage);
                }}
                disabled={currentPage === 0}
                className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              {(() => {
                const pageButtons = [];

                // Always show first page
                if (currentPage > 2) {
                  pageButtons.push(
                    <button
                      key={0}
                      onClick={() => {
                        handleChangePage(0);
                      }}
                      className="bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center px-4 py-1 border text-sm font-medium"
                    >
                      1
                    </button>
                  );

                  // Add ellipsis if needed
                  if (currentPage > 3) {
                    pageButtons.push(
                      <span
                        key="ellipsis1"
                        className="relative inline-flex items-center px-4 py-1 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>
                    );
                  }
                }

                // Calculate range of pages to show
                const startPage = Math.max(0, currentPage - 1);
                const endPage = Math.min(totalPages - 1, currentPage + 1);

                // Add current page range
                for (let i = startPage; i <= endPage; i++) {
                  pageButtons.push(
                    <button
                      key={i}
                      onClick={() => {
                        handleChangePage(i);
                      }}
                      className={`${
                        currentPage === i
                          ? "bg-indigo-50 border-indigo-500 text-indigo-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                      } relative inline-flex items-center px-4 py-1 border text-sm font-medium`}
                    >
                      {i + 1}
                    </button>
                  );
                }

                // Add ellipsis and last page if needed
                if (currentPage < totalPages - 3) {
                  pageButtons.push(
                    <span
                      key="ellipsis2"
                      className="relative inline-flex items-center px-4 py-1 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                    >
                      ...
                    </span>
                  );
                }

                if (currentPage < totalPages - 2 && totalPages > 1) {
                  pageButtons.push(
                    <button
                      key={totalPages - 1}
                      onClick={() => {
                        handleChangePage(totalPages - 1);
                      }}
                      className="bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-flex items-center px-4 py-1 border text-sm font-medium"
                    >
                      {totalPages}
                    </button>
                  );
                }

                return pageButtons;
              })()}
              <button
                onClick={() => {
                  const newPage = Math.min(totalPages - 1, currentPage + 1);
                  handleChangePage(newPage);
                }}
                disabled={currentPage === totalPages - 1 || totalPages === 0}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataGridGPT2;
